/** Local calendar key YYYY-MM-DD. String compare is chronological. */
export function toDateKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

export function addDays(dateKey, days) {
  const [y, m, d] = dateKey.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toDateKey(dt);
}

/** Accepts YYYY-MM-DD, ISO strings, Date, or Firestore timestamps. */
export function parseDateKey(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : toDateKey(parsed);
  }
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? toDateKey(d) : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : toDateKey(d);
  }
  if (value instanceof Date) return toDateKey(value);
  return null;
}

export function formatDateKey(dateKey) {
  if (!dateKey) return '';
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function statusOf(consent) {
  return String(consent?.status || '').toLowerCase();
}

function isSigned(consent) {
  const status = statusOf(consent);
  return status === 'signed' || status === 'completed';
}

function isQuestion(consent) {
  const status = statusOf(consent);
  return status === 'has_questions' || status === 'questions_requested';
}

function isPending(consent) {
  return ['pending', 'sent', 'opened', 'questions_answered'].includes(statusOf(consent));
}

function isDraft(consent) {
  return statusOf(consent) === 'draft';
}

function patientName(consent) {
  const fromParts = [consent?.patient?.firstName, consent?.patient?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return (consent?.patientName || fromParts || 'Patient').trim();
}

function treatmentLabel(consent) {
  return (consent?.treatmentFor || consent?.treatmentType || consent?.diagnosis || 'Treatment').trim();
}

function withinLastDays(dateKey, todayKey, days) {
  if (!dateKey) return false;
  return dateKey >= addDays(todayKey, -days) && dateKey <= todayKey;
}

const VISIBLE_ACTIONS = 8;

/**
 * Morning queue. Dated consents stay here until signed, so the practice
 * has a reason to open SmileConsent before the first patient sits down.
 */
export function buildTodayBoard({
  consents = [],
  unreadMessages = 0,
  unreadReviews = 0,
  now = new Date(),
} = {}) {
  const today = toDateKey(now);
  const yesterday = addDays(today, -1);
  const weekAhead = addDays(today, 7);
  const actions = [];
  const wins = [];
  const upcoming = [];

  consents.forEach((consent) => {
    if (!consent || consent.isDeleted || consent.isSampleData === true) return;
    const id = consent.id;
    if (!id) return;

    const name = patientName(consent);
    const treatment = treatmentLabel(consent);
    const needed = parseDateKey(consent.neededBy);
    const signed = isSigned(consent);
    const question = isQuestion(consent);
    const href = `/clinic/consents/${id}`;

    if (needed && !signed && needed <= today) {
      const overdue = needed < today;
      actions.push({
        id: `due-${id}`,
        priority: overdue ? 0 : 1,
        tone: overdue ? 'urgent' : 'today',
        title: overdue ? `${name} — consent overdue` : `${name} is in today`,
        detail: question
          ? `${treatment} — they asked a question and have not signed`
          : isDraft(consent)
            ? `${treatment} is still a draft`
            : statusOf(consent) === 'declined'
              ? `${treatment} was declined`
              : `${treatment} is not signed yet`,
        href: question ? `${href}?tab=message` : href,
        cta: question ? 'Reply' : 'Open consent',
      });
      return;
    }

    if (signed && !consent.clinicianSignature && needed && needed <= today) {
      actions.push({
        id: `clinician-${id}`,
        priority: 2,
        tone: 'clinician',
        title: `${name} is signed — clinician signature still needed`,
        detail: treatment,
        href: `/clinic/sign-consent/${id}`,
        cta: 'Sign',
      });
      return;
    }

    if (question) {
      actions.push({
        id: `question-${id}`,
        priority: 2,
        tone: 'question',
        title: `${name} asked a question`,
        detail: treatment,
        href: `${href}?tab=message`,
        cta: 'Reply',
      });
      return;
    }

    if (statusOf(consent) === 'declined') {
      const declinedOn = parseDateKey(consent.declinedAt) || parseDateKey(consent.updatedAt);
      if (!declinedOn || withinLastDays(declinedOn, today, 14)) {
        actions.push({
          id: `declined-${id}`,
          priority: 3,
          tone: 'declined',
          title: `${name} declined this consent`,
          detail: treatment,
          href,
          cta: 'Review',
        });
      }
      return;
    }

    if (needed && !signed && needed > today && needed <= weekAhead) {
      upcoming.push({
        id: `upcoming-${id}`,
        title: name,
        detail: `${treatment} · ${formatDateKey(needed)}`,
        href,
      });
      return;
    }

    if (isPending(consent)) {
      actions.push({
        id: `waiting-${id}`,
        priority: 5,
        tone: 'waiting',
        title: `${name} has not signed yet`,
        detail: needed ? `${treatment} · in on ${formatDateKey(needed)}` : treatment,
        href,
        cta: 'Open consent',
      });
    }

    if (signed) {
      const signedOn = parseDateKey(consent.signedAt) || parseDateKey(consent.updatedAt);
      if (signedOn === today || signedOn === yesterday) {
        wins.push({
          id: `signed-${id}`,
          title: name,
          detail: signedOn === today ? `${treatment} · signed today` : `${treatment} · signed yesterday`,
          href,
        });
      }
    }
  });

  if (unreadMessages > 0) {
    actions.push({
      id: 'messages',
      priority: 2,
      tone: 'question',
      title: unreadMessages === 1 ? '1 patient message is unread' : `${unreadMessages} patient messages are unread`,
      detail: 'Reply before the patient chases the practice',
      href: '/clinic/messages',
      cta: 'Open messages',
    });
  }

  if (unreadReviews > 0) {
    actions.push({
      id: 'reviews',
      priority: 4,
      tone: 'review',
      title:
        unreadReviews === 1
          ? '1 private review reply is waiting'
          : `${unreadReviews} private review replies are waiting`,
      detail: 'Concerns that should stay off Google',
      href: '/clinic/reviews',
      cta: 'Read feedback',
    });
  }

  actions.sort((a, b) => a.priority - b.priority || a.title.localeCompare(b.title));
  upcoming.sort((a, b) => a.detail.localeCompare(b.detail));

  return {
    actions: actions.slice(0, VISIBLE_ACTIONS),
    actionCount: actions.length,
    hiddenCount: Math.max(0, actions.length - VISIBLE_ACTIONS),
    wins: wins.slice(0, 4),
    upcoming: upcoming.slice(0, 4),
  };
}
